import React, { useState, useEffect } from "react";
import "./Metrics.css";
import * as ApiService from "../services/ApiService";
import DoctorChip from "./DoctorChip";
import OverallSeedCalls from "./OverallSeedCalls";
import {
  BsTable,
  BsBarChartFill,
  BsDownload,
  BsPlusCircle,
  BsCalculator,
  BsClipboardData,
  BsInfoCircle,
} from "react-icons/bs";
import {
  FormControl,
  Select,
  MenuItem,
  Button,
  ButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import moment from "moment";
import { useAuth0 } from "@auth0/auth0-react";

const Metrics = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedCallList, setSelectedCallList] = useState("");
  const [timePeriod, setTimePeriod] = useState("overall");
  const [viewMode, setViewMode] = useState("table");
  const [adjustForWeights, setAdjustForWeights] = useState(false);
  const [callLists, setCallLists] = useState([]);
  const [metricsData, setMetricsData] = useState([]);
  const [historicalWeightsForMonth, setHistoricalWeightsForMonth] = useState(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [showCatchUpModal, setShowCatchUpModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [medianValues, setMedianValues] = useState({
    regularWeekdays: 0,
    weekends: 0,
    publicHolidays: 0,
    total: 0,
  });
  const [existingArtificialCalls, setExistingArtificialCalls] = useState(null);
  const [doctorDetails, setDoctorDetails] = useState({});

  useEffect(() => {
    // Fetch call lists and available months on component mount
    const initializeData = async () => {
      try {
        const [lists, months] = await Promise.all([
          ApiService.fetchCallLists(getAccessTokenSilently),
          ApiService.getAvailableMonths(getAccessTokenSilently),
        ]);

        // Filter to only active call lists
        const activeCallLists = lists.filter((list) => list.active);

        // Set call lists
        setCallLists(activeCallLists);
        if (activeCallLists.length > 0) {
          setSelectedCallList(activeCallLists[0].callListTypeId);
        }

        // Set available months
        setAvailableMonths(months);
        setTimePeriod(months[0]); // Set to first option (overall)
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    // Fetch metrics data and potentially historical weights
    const fetchMetricsAndWeights = async () => {
      if (!selectedCallList) return;

      setLoading(true);
      setHistoricalWeightsForMonth({});

      try {
        let data;
        // Conditionally fetch data based on view settings
        if (timePeriod === "overall" && adjustForWeights) {
          // Fetch overall adjusted data using the new endpoint
          // console.log(
          //   "Fetching Overall Adjusted Metrics for:",
          //   selectedCallList
          // );
          data = await ApiService.getOverallAdjustedMetrics(
            getAccessTokenSilently,
            selectedCallList
          );
          // We don't need historical weights here as adjustment is done backend
          setHistoricalWeightsForMonth({});
        } else {
          // Fetch regular metrics data (either overall non-adjusted, or specific month)
          // console.log(
          //   "Fetching Regular Metrics for:",
          //   selectedCallList,
          //   timePeriod
          // );
          data = await ApiService.getMetrics(
            getAccessTokenSilently,
            selectedCallList,
            timePeriod
          );

          // Fetch historical weights ONLY if a specific month is selected for display purposes
          if (timePeriod !== "overall") {
            try {
              const monthStartDate = moment(timePeriod, "MMM YYYY")
                .startOf("month")
                .format("YYYY-MM-DD");
              // console.log("Fetching Historical Weights for:", monthStartDate);
              const weightsData = await ApiService.fetchHistoricalWeights(
                getAccessTokenSilently,
                monthStartDate
              );
              setHistoricalWeightsForMonth(weightsData || {});
            } catch (weightsError) {
              console.error("Error fetching historical weights:", weightsError);
              setHistoricalWeightsForMonth({});
            }
          } else {
            setHistoricalWeightsForMonth({}); // Clear if overall
          }
        }

        setMetricsData(data || []); // Ensure data is always an array
      } catch (error) {
        console.error("Error fetching metrics:", error);
        setMetricsData([]); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchMetricsAndWeights();
  }, [selectedCallList, timePeriod, adjustForWeights]);

  useEffect(() => {
    // Fetch doctor details when metrics data changes
    const fetchDoctorDetails = async () => {
      const details = {};
      for (const row of metricsData) {
        try {
          const doctorDetail = await ApiService.getDoctorDetails(
            getAccessTokenSilently,
            row.doctorId
          );
          details[row.doctorId] = doctorDetail;
        } catch (error) {
          console.error(
            `Error fetching details for doctor ${row.doctorId}:`,
            error
          );
        }
      }
      setDoctorDetails(details);
    };

    if (metricsData.length > 0) {
      fetchDoctorDetails();
    }
  }, [metricsData]);

  useEffect(() => {
    if (metricsData && metricsData.length > 0) {
      let regularWeekdayValues, weekendValues, publicHolidayValues;

      if (adjustForWeights) {
        const adjustedData = metricsData.map((d) => {
          if (d.adjustedRegularWeekdays !== undefined) {
            return {
              regularWeekdays:
                d.adjustedRegularWeekdays +
                (d.adjustedArtificialRegularWeekdays || 0),
              weekends:
                d.adjustedWeekends + (d.adjustedArtificialWeekends || 0),
              publicHolidays:
                d.adjustedPublicHolidays +
                (d.adjustedArtificialPublicHolidays || 0),
            };
          }

          let weightForRow = doctorDetails[d.doctorId]?.schedulingWeight;
          if (
            timePeriod !== "overall" &&
            historicalWeightsForMonth[d.doctorId] !== undefined
          ) {
            weightForRow = historicalWeightsForMonth[d.doctorId];
          }
          const calculationWeight =
            weightForRow !== undefined &&
            weightForRow !== null &&
            weightForRow !== 0
              ? weightForRow
              : 1;

          const totalRegularWeekdays =
            d.regularWeekdays + (d.artificialRegularWeekdays || 0);
          const totalWeekends = d.weekends + (d.artificialWeekends || 0);
          const totalPublicHolidays =
            d.publicHolidays + (d.artificialPublicHolidays || 0);

          return {
            regularWeekdays: getAdjustedValue(
              totalRegularWeekdays,
              calculationWeight
            ),
            weekends: getAdjustedValue(totalWeekends, calculationWeight),
            publicHolidays: getAdjustedValue(
              totalPublicHolidays,
              calculationWeight
            ),
          };
        });

        regularWeekdayValues = adjustedData.map((d) => d.regularWeekdays);
        weekendValues = adjustedData.map((d) => d.weekends);
        publicHolidayValues = adjustedData.map((d) => d.publicHolidays);
      } else {
        regularWeekdayValues = metricsData.map(
          (d) => d.regularWeekdays + (d.artificialRegularWeekdays || 0)
        );
        weekendValues = metricsData.map(
          (d) => d.weekends + (d.artificialWeekends || 0)
        );
        publicHolidayValues = metricsData.map(
          (d) => d.publicHolidays + (d.artificialPublicHolidays || 0)
        );
      }

      // Calculate total values for each doctor to get the median of totals
      const totalValues = metricsData.map((row) => {
        let weightForRow = doctorDetails[row.doctorId]?.schedulingWeight;
        if (
          timePeriod !== "overall" &&
          historicalWeightsForMonth[row.doctorId] !== undefined
        ) {
          weightForRow = historicalWeightsForMonth[row.doctorId];
        }
        const calculationWeight =
          weightForRow !== undefined &&
          weightForRow !== null &&
          weightForRow !== 0
            ? weightForRow
            : 1;

        return calculateTotalCalls(row, calculationWeight);
      });

      setMedianValues({
        regularWeekdays: calculateMedian(regularWeekdayValues),
        weekends: calculateMedian(weekendValues),
        publicHolidays: calculateMedian(publicHolidayValues),
        total: calculateMedian(totalValues),
      });
    } else {
      setMedianValues({
        regularWeekdays: 0,
        weekends: 0,
        publicHolidays: 0,
        total: 0,
      });
    }
  }, [
    metricsData,
    adjustForWeights,
    doctorDetails,
    historicalWeightsForMonth,
    timePeriod,
  ]);

  // Helper function to calculate median
  const calculateMedian = (values) => {
    if (!values.length) return 0;

    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      return (sorted[middle - 1] + sorted[middle]) / 2;
    } else {
      return sorted[middle];
    }
  };

  // Helper function to adjust value for weight
  const getAdjustedValue = (value, weight) => {
    // Check if the value is actually one of our pre-adjusted fields from backend
    if (
      typeof value === "object" &&
      value !== null &&
      value.adjusted !== undefined
    ) {
      return value.adjusted;
    }

    // Use a sensible default if weight is invalid or 0
    const validWeight =
      weight !== undefined && weight !== null && weight !== 0 ? weight : 1;
    return value * (1 / validWeight);
  };

  // Helper function to format adjusted values
  const formatAdjustedValue = (value, showFull = false) => {
    if (showFull) {
      // For tooltip: show up to 6 decimal places
      return value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      });
    }
    // For display: show up to 2 decimal places
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  // Helper function to calculate total calls (adjusted or not)
  const calculateTotalCalls = (row, weight) => {
    // If metrics data has the adjusted fields from the backend, use them
    if (
      adjustForWeights &&
      row.adjustedRegularWeekdays !== undefined &&
      row.adjustedWeekends !== undefined &&
      row.adjustedPublicHolidays !== undefined
    ) {
      // Use pre-calculated adjusted values from backend (historically weighted!)
      return (
        row.adjustedRegularWeekdays +
        row.adjustedWeekends +
        row.adjustedPublicHolidays +
        (row.adjustedArtificialRegularWeekdays || 0) +
        (row.adjustedArtificialWeekends || 0) +
        (row.adjustedArtificialPublicHolidays || 0)
      );
    }

    // Otherwise, fall back to the original calculation
    const regularWeekdays =
      row.regularWeekdays + (row.artificialRegularWeekdays || 0);
    const weekends = row.weekends + (row.artificialWeekends || 0);
    const publicHolidays =
      row.publicHolidays + (row.artificialPublicHolidays || 0);

    if (adjustForWeights) {
      // Use the provided weight for adjustment
      return (
        getAdjustedValue(regularWeekdays, weight) +
        getAdjustedValue(weekends, weight) +
        getAdjustedValue(publicHolidays, weight)
      );
    }
    // If not adjusting, just return the sum
    return regularWeekdays + weekends + publicHolidays;
  };

  const isBelowThreshold = (row) => {
    if (!row) return false;

    // Determine the correct weight to use for THIS row
    let weightForRow = doctorDetails[row.doctorId]?.schedulingWeight; // Default current
    if (
      timePeriod !== "overall" &&
      historicalWeightsForMonth[row.doctorId] !== undefined
    ) {
      weightForRow = historicalWeightsForMonth[row.doctorId]; // Use historical if available
    }
    weightForRow =
      weightForRow !== undefined && weightForRow !== null && weightForRow !== 0
        ? weightForRow
        : 1; // Ensure valid weight

    // Calculate total calls for the current row using the correct weight
    const totalCalls = calculateTotalCalls(row, weightForRow); // Pass the correct weight

    // Calculate adjusted median total based on all doctors' adjusted totals
    let medianTotal;
    if (adjustForWeights) {
      // Get all doctors' adjusted totals using their respective correct weights
      const adjustedTotals = metricsData.map((d) => {
        // If we have adjusted data from backend, use that
        if (
          d.adjustedRegularWeekdays !== undefined &&
          d.adjustedWeekends !== undefined &&
          d.adjustedPublicHolidays !== undefined
        ) {
          return calculateTotalCalls(d); // No weight needed, adjustment already done
        }

        // Otherwise use original calculation with correct weight
        let weightForMedianCalc = doctorDetails[d.doctorId]?.schedulingWeight; // Default current
        if (
          timePeriod !== "overall" &&
          historicalWeightsForMonth[d.doctorId] !== undefined
        ) {
          weightForMedianCalc = historicalWeightsForMonth[d.doctorId]; // Use historical if available
        }
        weightForMedianCalc =
          weightForMedianCalc !== undefined &&
          weightForMedianCalc !== null &&
          weightForMedianCalc !== 0
            ? weightForMedianCalc
            : 1; // Ensure valid weight
        // Calculate total for this doctor using their correct weight
        return calculateTotalCalls(d, weightForMedianCalc); // Pass the correct weight
      });
      medianTotal = calculateMedian(adjustedTotals);
    } else {
      // Use the calculated median total
      medianTotal = medianValues.total;
    }

    const threshold = medianTotal * 0.6;
    return totalCalls < threshold;
  };

  const downloadCSV = () => {
    // Create CSV content
    const headers = [
      "Doctor",
      "Regular Weekdays",
      "Weekends",
      "Public Holidays",
      "Total",
    ];
    const csvRows = [headers];

    metricsData.forEach((row) => {
      csvRows.push([
        row.doctorAbbreviation,
        row.regularWeekdays,
        row.weekends,
        row.publicHolidays,
        row.regularWeekdays + row.weekends + row.publicHolidays,
      ]);
    });

    // Get the call list name from the callLists array
    const selectedList = callLists.find(
      (list) => list.callListTypeId === selectedCallList
    );
    const callListName = selectedList
      ? selectedList.name.replace(/\s+/g, "")
      : selectedCallList;

    const csvContent = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `call-metrics-${callListName}-${timePeriod}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenCatchUpModal = async (doctor) => {
    setSelectedDoctor(doctor);

    try {
      // For overall view, yearMonth is null to fetch all calls for the doctor
      const existingCalls = await ApiService.getArtificialCalls(
        getAccessTokenSilently,
        doctor.doctorId,
        selectedCallList,
        null
      );
      setExistingArtificialCalls(existingCalls || null);
    } catch (error) {
      console.error("Error fetching existing artificial calls:", error);
      setExistingArtificialCalls(null);
    }

    setShowCatchUpModal(true);
  };

  const handleCloseCatchUpModal = () => {
    setShowCatchUpModal(false);
    setSelectedDoctor(null);
    setExistingArtificialCalls(null);
  };

  const handleApplyOverallCatchUp = async (updates) => {
    if (!updates) return;

    try {
      await ApiService.batchUpdateArtificialCalls(
        getAccessTokenSilently,
        updates
      );

      // Refresh metrics data after applying changes
      const currentAdjustForWeights = adjustForWeights; // Store current state
      setAdjustForWeights(false); // Temporarily set to false to fetch raw data
      const updatedMetrics = await ApiService.getMetrics(
        getAccessTokenSilently,
        selectedCallList,
        timePeriod
      );
      setMetricsData(updatedMetrics || []);
      setAdjustForWeights(currentAdjustForWeights); // Restore original state

      alert("Overall catch-up calls applied successfully!");
    } catch (error) {
      console.error("Error applying overall catch-up calls:", error);
      alert(
        "Failed to apply overall catch-up calls: " +
          (error.message || "Unknown error")
      );
    }
  };

  const renderCatchUpModal = () => {
    if (!showCatchUpModal || !selectedDoctor) return null;

    // The catch-up modal is now only shown for the "overall" time period.
    if (timePeriod === "overall") {
      return (
        <OverallSeedCalls
          doctor={selectedDoctor}
          availableMonths={availableMonths.filter((m) => m !== "overall")}
          medianValues={medianValues}
          onApply={handleApplyOverallCatchUp}
          onClose={handleCloseCatchUpModal}
          existingArtificialCalls={existingArtificialCalls}
          callListId={selectedCallList}
        />
      );
    }

    // No modal for monthly views.
    return null;
  };

  const renderTableView = () => {
    // Get the call list name
    const selectedListName =
      callLists.find((list) => list.callListTypeId === selectedCallList)
        ?.name || "";

    return (
      <div className="table-container">
        <table className="metrics-table">
          <thead>
            <tr>
              <th>Doctor</th>
              <th className="number-column">Regular Weekdays</th>
              <th className="number-column">Weekends</th>
              <th className="number-column">Public Holidays</th>
              <th className="number-column">Total</th>
              {timePeriod === "overall" && (
                <th className="number-column">
                  <span
                    className="scheduling-weight-header"
                    onMouseEnter={(e) => {
                      const tooltip = document.createElement("div");
                      tooltip.className = "weight-tooltip seed-calls-tooltip";
                      tooltip.innerHTML =
                        "Seed artificial calls to help doctors catch up to the median call count.<br><br>This is useful when adding new doctors to the system or when doctors have missed calls due to leave or other circumstances.";
                      document.body.appendChild(tooltip);
                      const rect = e.target.getBoundingClientRect();
                      const scrollTop =
                        window.pageYOffset ||
                        document.documentElement.scrollTop;
                      const scrollLeft =
                        window.pageXOffset ||
                        document.documentElement.scrollLeft;
                      // Position tooltip to the left of the text
                      tooltip.style.right = `${
                        window.innerWidth - rect.left + 5
                      }px`;
                      tooltip.style.left = "auto"; // Clear left position
                      tooltip.style.top = `${rect.top + scrollTop}px`;
                    }}
                    onMouseLeave={() => {
                      const tooltip = document.querySelector(".weight-tooltip");
                      if (tooltip) tooltip.remove();
                    }}
                  >
                    Seed Calls
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {/* Median Row */}
            <tr className="median-row">
              <td>
                <span className="median-label">Median</span>
              </td>
              <td className="number-column">
                {adjustForWeights
                  ? formatAdjustedValue(medianValues.regularWeekdays)
                  : Math.round(medianValues.regularWeekdays)}
              </td>
              <td className="number-column">
                {adjustForWeights
                  ? formatAdjustedValue(medianValues.weekends)
                  : Math.round(medianValues.weekends)}
              </td>
              <td className="number-column">
                {adjustForWeights
                  ? formatAdjustedValue(medianValues.publicHolidays)
                  : Math.round(medianValues.publicHolidays)}
              </td>
              <td className="number-column">
                {adjustForWeights
                  ? formatAdjustedValue(medianValues.total)
                  : Math.round(medianValues.total)}
              </td>
              {timePeriod === "overall" && <td className="number-column"></td>}
            </tr>
            {metricsData.map((row) => {
              // Determine which weight to display
              let displayWeight = doctorDetails[row.doctorId]?.schedulingWeight;
              let weightSource = "current";
              if (
                timePeriod !== "overall" &&
                historicalWeightsForMonth[row.doctorId] !== undefined
              ) {
                displayWeight = historicalWeightsForMonth[row.doctorId];
                weightSource = "historical";
              }
              const calculationWeight =
                displayWeight !== undefined &&
                displayWeight !== null &&
                displayWeight !== 0
                  ? displayWeight
                  : 1;
              const isBelowMedianThreshold = isBelowThreshold(row);

              // Determine if we are in the special "Overall Adjusted" mode
              const isOverallAdjustedMode =
                timePeriod === "overall" && adjustForWeights;

              return (
                <tr key={row.doctorId}>
                  <td>
                    <div className="doctor-info">
                      <DoctorChip
                        doctor={{
                          staffId: row.doctorId,
                          abbreviation: row.doctorAbbreviation,
                        }}
                        disableTooltip={false}
                      />
                      {displayWeight !== undefined && (
                        <span
                          className="scheduling-weight"
                          title={`Scheduling Weight (${weightSource})`}
                          data-weight={displayWeight}
                        >
                          {Number(displayWeight).toLocaleString("en-US", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 3,
                          })}
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    className={`number-column ${
                      isBelowMedianThreshold ? "below-threshold" : ""
                    }`}
                    title={
                      adjustForWeights && !isOverallAdjustedMode
                        ? formatAdjustedValue(
                            getAdjustedValue(
                              row.regularWeekdays,
                              calculationWeight
                            ),
                            true
                          )
                        : undefined
                    }
                  >
                    {adjustForWeights
                      ? // If we have backend-calculated metrics, use them directly
                        row.adjustedRegularWeekdays !== undefined
                        ? formatAdjustedValue(row.adjustedRegularWeekdays)
                        : // Otherwise calculate on the fly
                          formatAdjustedValue(
                            getAdjustedValue(
                              row.regularWeekdays,
                              calculationWeight
                            )
                          )
                      : row.regularWeekdays}
                    {/* Artificial call display for Regular Weekdays */}
                    {isOverallAdjustedMode
                      ? row.adjustedArtificialRegularWeekdays > 0 && (
                          <span className="artificial-call artificial-weekday">
                            {" "}
                            (+
                            {formatAdjustedValue(
                              row.adjustedArtificialRegularWeekdays
                            )}
                            )
                          </span>
                        )
                      : row.artificialRegularWeekdays > 0 && (
                          <span className="artificial-call artificial-weekday">
                            {" "}
                            (+
                            {adjustForWeights
                              ? formatAdjustedValue(
                                  getAdjustedValue(
                                    row.artificialRegularWeekdays,
                                    calculationWeight
                                  )
                                )
                              : row.artificialRegularWeekdays}
                            )
                          </span>
                        )}
                  </td>
                  <td
                    className={`number-column ${
                      isBelowMedianThreshold ? "below-threshold" : ""
                    }`}
                    title={
                      adjustForWeights && !isOverallAdjustedMode
                        ? formatAdjustedValue(
                            getAdjustedValue(row.weekends, calculationWeight),
                            true
                          )
                        : undefined
                    }
                  >
                    {adjustForWeights
                      ? row.adjustedWeekends !== undefined
                        ? formatAdjustedValue(row.adjustedWeekends)
                        : formatAdjustedValue(
                            getAdjustedValue(row.weekends, calculationWeight)
                          )
                      : row.weekends}
                    {/* Artificial call display for Weekends */}
                    {isOverallAdjustedMode
                      ? row.adjustedArtificialWeekends > 0 && (
                          <span className="artificial-call artificial-weekend">
                            {" "}
                            (+
                            {formatAdjustedValue(
                              row.adjustedArtificialWeekends
                            )}
                            )
                          </span>
                        )
                      : row.artificialWeekends > 0 && (
                          <span className="artificial-call artificial-weekend">
                            {" "}
                            (+
                            {adjustForWeights
                              ? formatAdjustedValue(
                                  getAdjustedValue(
                                    row.artificialWeekends,
                                    calculationWeight
                                  )
                                )
                              : row.artificialWeekends}
                            )
                          </span>
                        )}
                  </td>
                  <td
                    className={`number-column ${
                      isBelowMedianThreshold ? "below-threshold" : ""
                    }`}
                    title={
                      adjustForWeights && !isOverallAdjustedMode
                        ? formatAdjustedValue(
                            getAdjustedValue(
                              row.publicHolidays,
                              calculationWeight
                            ),
                            true
                          )
                        : undefined
                    }
                  >
                    {adjustForWeights
                      ? row.adjustedPublicHolidays !== undefined
                        ? formatAdjustedValue(row.adjustedPublicHolidays)
                        : formatAdjustedValue(
                            getAdjustedValue(
                              row.publicHolidays,
                              calculationWeight
                            )
                          )
                      : row.publicHolidays}
                    {/* Artificial call display for Public Holidays */}
                    {isOverallAdjustedMode
                      ? row.adjustedArtificialPublicHolidays > 0 && (
                          <span className="artificial-call artificial-holiday">
                            {" "}
                            (+
                            {formatAdjustedValue(
                              row.adjustedArtificialPublicHolidays
                            )}
                            )
                          </span>
                        )
                      : row.artificialPublicHolidays > 0 && (
                          <span className="artificial-call artificial-holiday">
                            {" "}
                            (+
                            {adjustForWeights
                              ? formatAdjustedValue(
                                  getAdjustedValue(
                                    row.artificialPublicHolidays,
                                    calculationWeight
                                  )
                                )
                              : row.artificialPublicHolidays}
                            )
                          </span>
                        )}
                  </td>
                  <td
                    className={`number-column ${
                      isBelowThreshold(row) ? "below-threshold" : ""
                    }`}
                  >
                    {adjustForWeights
                      ? row.adjustedRegularWeekdays !== undefined
                        ? // Use pre-calculated totals from backend
                          formatAdjustedValue(
                            row.adjustedRegularWeekdays +
                              row.adjustedWeekends +
                              row.adjustedPublicHolidays +
                              (row.adjustedArtificialRegularWeekdays || 0) +
                              (row.adjustedArtificialWeekends || 0) +
                              (row.adjustedArtificialPublicHolidays || 0)
                          )
                        : // Calculate on the fly using weight
                          formatAdjustedValue(
                            calculateTotalCalls(row, calculationWeight)
                          )
                      : calculateTotalCalls(row, 1)}
                    {/* Artificial call display for Total */}
                    {isOverallAdjustedMode
                      ? (row.adjustedArtificialRegularWeekdays > 0 ||
                          row.adjustedArtificialWeekends > 0 ||
                          row.adjustedArtificialPublicHolidays > 0) && (
                          <span className="artificial-call">
                            {" "}
                            (+
                            {formatAdjustedValue(
                              (row.adjustedArtificialRegularWeekdays || 0) +
                                (row.adjustedArtificialWeekends || 0) +
                                (row.adjustedArtificialPublicHolidays || 0)
                            )}
                            )
                          </span>
                        )
                      : (row.artificialRegularWeekdays > 0 ||
                          row.artificialWeekends > 0 ||
                          row.artificialPublicHolidays > 0) && (
                          <span className="artificial-call">
                            {" "}
                            (+
                            {adjustForWeights
                              ? formatAdjustedValue(
                                  getAdjustedValue(
                                    (row.artificialRegularWeekdays || 0) +
                                      (row.artificialWeekends || 0) +
                                      (row.artificialPublicHolidays || 0),
                                    calculationWeight
                                  )
                                )
                              : (row.artificialRegularWeekdays || 0) +
                                (row.artificialWeekends || 0) +
                                (row.artificialPublicHolidays || 0)}
                            )
                          </span>
                        )}
                  </td>
                  {timePeriod === "overall" && (
                    <td className="number-column">
                      <button
                        className={`seed-calls-button ${
                          isBelowThreshold(row) ? "below-threshold" : ""
                        }`}
                        onClick={() => handleOpenCatchUpModal(row)}
                      >
                        Seed Calls
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChartView = () => {
    // Check if we have adjusted data from backend
    const hasAdjustedData =
      metricsData.length > 0 &&
      metricsData[0].adjustedRegularWeekdays !== undefined;

    const chartData = metricsData.map((row) => {
      let weightForChart = doctorDetails[row.doctorId]?.schedulingWeight;
      if (
        timePeriod !== "overall" &&
        historicalWeightsForMonth[row.doctorId] !== undefined
      ) {
        weightForChart = historicalWeightsForMonth[row.doctorId];
      }
      weightForChart =
        weightForChart !== undefined &&
        weightForChart !== null &&
        weightForChart !== 0
          ? weightForChart
          : 1;

      const displayName = `${row.doctorAbbreviation} (${Number(
        // Display correct weight (historical or current)
        timePeriod !== "overall" &&
          historicalWeightsForMonth[row.doctorId] !== undefined
          ? historicalWeightsForMonth[row.doctorId]
          : doctorDetails[row.doctorId]?.schedulingWeight ?? "N/A" // Show current or N/A for overall
      ).toLocaleString("en-US", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 3,
      })})`;

      // If we have backend-calculated adjustments, use them
      if (adjustForWeights && hasAdjustedData) {
        return {
          doctorAbbreviation: displayName,
          regularWeekdays: row.adjustedRegularWeekdays,
          artificialRegularWeekdays: row.adjustedArtificialRegularWeekdays || 0,
          weekends: row.adjustedWeekends,
          artificialWeekends: row.adjustedArtificialWeekends || 0,
          publicHolidays: row.adjustedPublicHolidays,
          artificialPublicHolidays: row.adjustedArtificialPublicHolidays || 0,
        };
      } else {
        // Otherwise, calculate potentially adjusted values for month view or true count overall
        return {
          doctorAbbreviation: displayName,
          regularWeekdays: adjustForWeights
            ? getAdjustedValue(row.regularWeekdays, weightForChart)
            : row.regularWeekdays,
          artificialRegularWeekdays: adjustForWeights
            ? getAdjustedValue(
                row.artificialRegularWeekdays || 0,
                weightForChart
              )
            : row.artificialRegularWeekdays || 0,
          weekends: adjustForWeights
            ? getAdjustedValue(row.weekends, weightForChart)
            : row.weekends,
          artificialWeekends: adjustForWeights
            ? getAdjustedValue(row.artificialWeekends || 0, weightForChart)
            : row.artificialWeekends || 0,
          publicHolidays: adjustForWeights
            ? getAdjustedValue(row.publicHolidays, weightForChart)
            : row.publicHolidays,
          artificialPublicHolidays: adjustForWeights
            ? getAdjustedValue(
                row.artificialPublicHolidays || 0,
                weightForChart
              )
            : row.artificialPublicHolidays || 0,
        };
      }
    });

    return (
      <div style={{ width: "100%", height: 800 }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 20, left: 120, bottom: 40 }}
          >
            <XAxis
              type="number"
              domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
              tickCount={6}
              axisLine={true}
              tickLine={true}
              stroke="#666"
              tickFormatter={(value) =>
                adjustForWeights
                  ? formatAdjustedValue(value)
                  : Math.round(value)
              }
            >
              <Label
                value={`Number of Calls${
                  adjustForWeights ? " (Adjusted for Weights)" : ""
                }`}
                position="bottom"
                offset={10}
                style={{
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              />
            </XAxis>
            <YAxis
              dataKey="doctorAbbreviation"
              type="category"
              width={120}
              axisLine={false}
              tickLine={false}
              stroke="#666"
              interval={0}
              fontSize={14}
              style={{
                fontWeight: "bold",
              }}
              tickFormatter={(value) => value.replace(/\n/g, " ")}
            />
            <Tooltip
              formatter={(value) => formatAdjustedValue(value)}
              labelFormatter={(label) => `${label}`}
              cursor={{ fill: "transparent" }}
            />
            <Legend
              verticalAlign="top"
              height={50}
              wrapperStyle={{
                paddingLeft: 10,
                paddingRight: 10,
              }}
              iconSize={15}
              formatter={(value) => value}
            />
            <Bar
              dataKey="regularWeekdays"
              stackId="a"
              fill="rgb(105, 150, 115)"
              name="Regular Weekdays"
            />
            <Bar
              dataKey="artificialRegularWeekdays"
              stackId="a"
              fill="rgb(75, 110, 85)"
              name="Artificial Weekdays"
            />
            <Bar
              dataKey="weekends"
              stackId="a"
              fill="#4d94ff"
              name="Weekends"
            />
            <Bar
              dataKey="artificialWeekends"
              stackId="a"
              fill="#3a70c0"
              name="Artificial Weekends"
            />
            <Bar
              dataKey="publicHolidays"
              stackId="a"
              fill="#ffb366"
              name="Public Holidays"
            />
            <Bar
              dataKey="artificialPublicHolidays"
              stackId="a"
              fill="#d98c3e"
              name="Artificial Holidays"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="metrics-container">
      <div className="page-header">
        <div className="header-with-toggle">
          <h2>Metrics</h2>
        </div>
      </div>

      {callLists.length === 0 ? (
        <Paper style={{ padding: "20px", textAlign: "center" }}>
          <p style={{ color: "#666", fontSize: "16px" }}>
            No active call lists available.
          </p>
        </Paper>
      ) : (
        <>
          <div className="metrics-controls">
            <div className="call-list-buttons">
              {callLists.map((list) => (
                <span
                  key={list.callListTypeId}
                  className={`doctor-chip selectable ${
                    selectedCallList === list.callListTypeId ? "selected" : ""
                  }`}
                  onClick={() => setSelectedCallList(list.callListTypeId)}
                >
                  {list.name}
                </span>
              ))}
            </div>

            <div className="time-and-view-controls">
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                className="time-period-select"
              >
                {availableMonths.map((month) => (
                  <option key={month} value={month}>
                    {month === "overall" ? "Overall" : month}
                  </option>
                ))}
              </select>

              {!loading && metricsData.length > 0 && (
                <>
                  <button
                    className="download-button"
                    onClick={downloadCSV}
                    title="Download CSV"
                  >
                    <BsDownload /> CSV
                  </button>
                  <div className="view-controls-group">
                    <div className="count-type-toggle">
                      <span
                        className="info-icon"
                        onMouseEnter={(e) => {
                          const tooltip = document.createElement("div");
                          tooltip.className = "weight-tooltip";
                          tooltip.innerHTML = `
                            <strong>True Count:</strong> Shows actual number of calls assigned.<br/><br/>
                            <strong>Adjusted for Weights:</strong> Shows calls adjusted by scheduling weight (e.g. if weight is 0.5, calls are doubled).
                          `;
                          document.body.appendChild(tooltip);
                          const rect = e.target.getBoundingClientRect();
                          const scrollTop =
                            window.pageYOffset ||
                            document.documentElement.scrollTop;
                          const scrollLeft =
                            window.pageXOffset ||
                            document.documentElement.scrollLeft;
                          tooltip.style.left = `${
                            rect.right + scrollLeft + 5
                          }px`;
                          tooltip.style.top = `${rect.top + scrollTop}px`;
                        }}
                        onMouseLeave={() => {
                          const tooltip =
                            document.querySelector(".weight-tooltip");
                          if (tooltip) tooltip.remove();
                        }}
                      >
                        <BsInfoCircle />
                      </span>
                      <button
                        className={!adjustForWeights ? "active" : ""}
                        onClick={() => setAdjustForWeights(false)}
                      >
                        <BsClipboardData /> True Count
                      </button>
                      <button
                        className={adjustForWeights ? "active" : ""}
                        onClick={() => setAdjustForWeights(true)}
                      >
                        <BsCalculator /> Adjusted for Weights
                      </button>
                    </div>
                    <div className="view-toggle-group">
                      <button
                        className={`view-button ${
                          viewMode === "table" ? "active" : ""
                        }`}
                        onClick={() => setViewMode("table")}
                      >
                        <BsTable /> Table
                      </button>
                      <button
                        className={`view-button ${
                          viewMode === "chart" ? "active" : ""
                        }`}
                        onClick={() => setViewMode("chart")}
                      >
                        <BsBarChartFill /> Chart
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : metricsData.length === 0 ? (
            <Paper style={{ padding: "20px", textAlign: "center" }}>
              <p style={{ color: "#666", fontSize: "16px" }}>
                No call data available.
              </p>
            </Paper>
          ) : viewMode === "table" ? (
            renderTableView()
          ) : (
            renderChartView()
          )}
        </>
      )}

      {renderCatchUpModal()}
    </div>
  );
};

export default Metrics;
